import jsPDF from 'jspdf';
import { Event } from '@/models/event';
import { Firestore } from 'firebase/firestore';
import { BRAND_COLOR, FONT_HELCH, PaperSize, PAPER_DIMENSIONS } from '../pdf-constants';
import { drawImageCover, drawImageContain, fetchMonthImages, fetchClubInfo } from '../pdf-utils';
import { toAdHocPacificDate, getPacificMonthKey } from '../../date-utils';

interface PageContent {
    type: 'cover' | 'back' | 'month-img' | 'month-grid' | 'blank' | 'notes';
    monthKey?: string;
    monthName?: string;
    year?: string;
    events?: Event[];
    imageUrl?: string;
    clubInfo?: any; // Passed to cover/back
}

export async function exportSaddleStitchFormat(
    events: Event[],
    firestore: Firestore,
    paperSize: PaperSize = 'letter',
    images?: Record<string, string>,
    year?: number
) {
    // 1. Get Dims in Inches
    const dim = PAPER_DIMENSIONS[paperSize];
    let sheetW = dim.width;
    let sheetH = dim.height;
    if (dim.unit === 'mm') {
        sheetW /= 25.4;
        sheetH /= 25.4;
    }

    // 2. Face Dimensions (Portrait Sheet split horizontally)
    // Sheet is Portrait (e.g. 11x17). Face is Top half (11x8.5).
    const faceW = sheetW;
    const faceH = sheetH / 2;

    // 3. Scaling Factor (Baseline: Tabloid 11x17 -> Face 11x8.5)
    // We scale fonts and padding based on width ratio.
    const scale = faceW / 11;

    // Initialize Doc
    // Note: jsPDF constructor 'format' expects [w, h] in specified unit.
    // We use 'in' and pass our calculated inches.
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [sheetW, sheetH]
    });

    // Prepare Events Check (Pacific Time)
    const futureEvents = events.filter(e => {
        const today = new Date(); // Only for cutoff, use system time is fine or convert?
        // Let's use system time for "Now" but Pacific for "Event".
        // Actually, just keep simple future check.
        const d = new Date(e.date);
        return d.getTime() >= Date.now() - 86400000;    // 2. Group events by Month (Pacific Time)
    });
    const eventsByMonth: Record<string, Event[]> = {};
    const years = new Set<string>();

    events.forEach(e => {
        const key = getPacificMonthKey(e.date);
        const eventYear = key.split('-')[0];

        // precise filter: if year arg is provided, skip events not in that year
        if (year && parseInt(eventYear) !== year) return;

        years.add(eventYear);
        if (!eventsByMonth[key]) eventsByMonth[key] = [];
        eventsByMonth[key].push(e);
    });

    // Ensure full year (01-12) for the target year (or all found years)
    const targetYears = year ? [String(year)] : Array.from(years);

    targetYears.forEach(y => {
        for (let m = 1; m <= 12; m++) {
            const key = `${y}-${String(m).padStart(2, '0')}`;
            if (!eventsByMonth[key]) {
                eventsByMonth[key] = [];
            }
        }
    });

    const sortedMonths = Object.keys(eventsByMonth).sort();

    // Determine Start Date (First event month or Current Month)
    let startY = new Date().getFullYear();
    let startM = new Date().getMonth() + 1; // 1-based

    if (sortedMonths.length > 0) {
        [startY, startM] = sortedMonths[0].split('-').map(Number);
    }

    // Generate 12 Consecutive Month Keys
    const targetMonthKeys: string[] = [];
    let curY = startY;
    let curM = startM;
    for (let i = 0; i < 12; i++) {
        targetMonthKeys.push(`${curY}-${String(curM).padStart(2, '0')}`);
        curM++;
        if (curM > 12) {
            curM = 1;
            curY++;
        }
    }

    // Pre-fetch images and club info
    const [mediaMap, clubInfo] = await Promise.all([
        images ? Promise.resolve(images) : fetchMonthImages(firestore, targetMonthKeys),
        fetchClubInfo(firestore)
    ]);

    // Logical Pages (1..28)
    const pages: PageContent[] = [];

    // P1 Cover
    pages.push({ type: 'cover', clubInfo });

    // P2..25 Months (12 Spreads)
    for (const k of targetMonthKeys) {
        const [y, m] = k.split('-');
        const date = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
        const mName = date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

        // Img Page
        pages.push({
            type: 'month-img',
            monthKey: k,
            monthName: mName,
            year: y,
            imageUrl: mediaMap[k],
            clubInfo
        });

        // Grid Page
        pages.push({
            type: 'month-grid',
            monthKey: k,
            monthName: mName,
            year: y,
            events: eventsByMonth[k] || [],
            clubInfo
        });
    }

    // P26, P27 Notes
    pages.push({ type: 'notes' });
    pages.push({ type: 'notes' });

    // P28 Back
    pages.push({ type: 'back', clubInfo });

    // Ensure 28 pages
    if (pages.length !== 28) {
        console.warn(`Page count mismatch: ${pages.length}. Should be 28. Structure might be broken.`);
    }

    // Render Helper
    const renderFace = async (pageIdx: number, x: number, y: number, rotate: boolean) => {
        const content = pages[pageIdx];
        if (!content) return;

        doc.saveGraphicsState();

        // Rotation Transform
        if (rotate) {
            const pdfH = sheetH * 72;
            const centerX = (x + faceW / 2) * 72;
            const centerY = pdfH - ((y + faceH / 2) * 72);
            // @ts-ignore
            doc.internal.write(`-1 0 0 -1 ${(2 * centerX).toFixed(2)} ${(2 * centerY).toFixed(2)} cm`);
        }

        switch (content.type) {
            case 'cover':
                // White Background
                doc.setFillColor(255, 255, 255);
                doc.rect(x, y, faceW, faceH, 'F');

                // Logo (Centered, Contain)
                if (content.clubInfo?.clubLogoUrl) {
                    try {
                        const logoMargin = 1 * scale;
                        await drawImageContain(doc, content.clubInfo.clubLogoUrl, x + faceW / 4, y + logoMargin, faceW / 2, faceH / 2);
                    } catch (e) { }
                } else {
                    // Fallback Text
                    doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
                    doc.setFontSize(60 * scale);
                    doc.setFont(FONT_HELCH, 'bold');
                    doc.text("SRAC", x + faceW / 2, y + faceH / 2 - (1 * scale), { align: 'center' });
                }

                doc.setTextColor(0, 0, 0);
                doc.setFontSize(24 * scale);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text(`${new Date().getFullYear()} Calendar`, x + faceW / 2, y + faceH / 2 + (1.5 * scale), { align: 'center' });

                // Red Bar at Bottom
                const barH = 2 * scale;
                doc.setFillColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
                doc.rect(x, y + faceH - barH, faceW, barH, 'F');

                // Info in Bar
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(18 * scale);
                doc.setFont(FONT_HELCH, 'bold');

                if (content.clubInfo) {
                    let yPos = y + faceH - barH + (0.8 * scale);
                    if (content.clubInfo.clubWebsite) {
                        doc.text(content.clubInfo.clubWebsite.replace(/^https?:\/\//, ''), x + faceW / 2, yPos, { align: 'center' });
                        yPos += (0.4 * scale);
                    }
                    if (content.clubInfo.clubEmail) {
                        doc.setFontSize(14 * scale);
                        doc.setFont(FONT_HELCH, 'normal');
                        doc.text(content.clubInfo.clubEmail, x + faceW / 2, yPos, { align: 'center' });
                    }
                }
                break;

            case 'back':
                // White Background
                doc.setFillColor(255, 255, 255);
                doc.rect(x, y, faceW, faceH, 'F');

                // Logo (Small)
                if (content.clubInfo?.clubLogoUrl) {
                    try {
                        const logoSize = 3 * scale;
                        await drawImageContain(doc, content.clubInfo.clubLogoUrl, x + faceW / 2 - (logoSize / 2), y + (2 * scale), logoSize, logoSize);
                    } catch (e) { }
                }

                doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
                doc.setFontSize(24 * scale);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text("Snohomish Radio Aero Club", x + faceW / 2, y + (6 * scale), { align: 'center' });

                doc.setFontSize(14 * scale);
                doc.setTextColor(100, 100, 100);
                if (content.clubInfo?.clubWebsite) {
                    doc.text(content.clubInfo.clubWebsite, x + faceW / 2, y + (6.5 * scale), { align: 'center' });
                }
                break;

            case 'month-img':
                if (content.imageUrl) {
                    await drawImageCover(doc, content.imageUrl, x, y, faceW, faceH);
                    // Overlay Month Name
                    const bannerH = 1.5 * scale;
                    doc.setFillColor(0, 0, 0);
                    doc.setGState(new (doc as any).GState({ opacity: 0.4 }));
                    doc.rect(x, y + faceH - bannerH, faceW, bannerH, 'F');
                    doc.setGState(new (doc as any).GState({ opacity: 1 }));

                    doc.setTextColor(255, 255, 255);
                    doc.setFontSize(40 * scale);
                    doc.setFont(FONT_HELCH, 'bold');
                    doc.text(content.monthName || '', x + (0.5 * scale), y + faceH - (0.4 * scale));

                    doc.setFontSize(20 * scale);
                    doc.text(content.year || '', x + faceW - (0.5 * scale), y + faceH - (0.4 * scale), { align: 'right' });
                } else {
                    // Fallback
                    doc.setFillColor(240, 240, 240);
                    doc.rect(x, y, faceW, faceH, 'F');
                    doc.setTextColor(150, 150, 150);
                    doc.setFontSize(24 * scale);
                    doc.text(content.monthName || 'No Image', x + faceW / 2, y + faceH / 2, { align: 'center' });
                }
                break;

            case 'month-grid':
                // Layout Config
                const padding = 0.5 * scale;
                const footerH = 0.8 * scale;

                const contentX = x + padding;
                const contentY = y + padding;
                const contentW = faceW - (padding * 2);
                const contentH = faceH - (padding * 2) - footerH;

                // 1. Month Header
                doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
                doc.setFontSize(14 * scale);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text(`${content.monthName} ${content.year}`, contentX, contentY + (0.2 * scale));

                // 2. Days Header
                const daysY = contentY + (0.4 * scale);
                const headH = 0.3 * scale;
                doc.setFillColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
                doc.rect(contentX, daysY, contentW, headH, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(10 * scale);
                doc.setFont(FONT_HELCH, 'bold');

                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                const colW = contentW / 7;
                days.forEach((d, i) => {
                    doc.text(d, contentX + i * colW + colW / 2, daysY + (0.2 * scale), { align: 'center' });
                });

                // 3. Grid
                const gridY = daysY + headH;

                let numRows = 6;
                let daysInMonth = 31;
                let firstDay = 0;

                if (content.monthKey) {
                    const [yStr, mStr] = content.monthKey.split('-');
                    const year = parseInt(yStr);
                    const month = parseInt(mStr);
                    firstDay = new Date(year, month - 1, 1).getDay();
                    daysInMonth = new Date(year, month, 0).getDate();

                    // Dynamic Rows Calculation
                    // If spans 6 weeks: (firstDay + days) > 35?
                    // 0 + 28 = 28 (4 rows)
                    // 5 (Fri) + 31 = 36 (6 rows!)
                    // 6 (Sat) + 30 = 36 (6 rows)
                    // 6 (Sat) + 31 = 37 (6 rows)
                    numRows = Math.ceil((firstDay + daysInMonth) / 7);
                }

                // Grid Height - use approx 70% of space, but adapt to squareness?
                // Fixed Grid Area allows list to be consistent? 
                // User wants "more square". 
                // Providing a larger gridH area allows taller cells.
                const gridH = contentH * 0.70;
                const rowH = gridH / numRows;

                const eventsList: Event[] = [];
                let curDate = 1;

                doc.setLineWidth(0.005);
                doc.setDrawColor(200, 200, 200);

                for (let r = 0; r < numRows; r++) {
                    for (let c = 0; c < 7; c++) {
                        const cellX = contentX + c * colW;
                        const cellY = gridY + r * rowH;

                        // Check valid date
                        let isValidDate = true;
                        if (r === 0 && c < firstDay) isValidDate = false;
                        else if (curDate > daysInMonth) isValidDate = false;

                        if (!isValidDate) {
                            doc.setFillColor(245, 245, 245);
                            doc.rect(cellX, cellY, colW, rowH, 'FD');
                        } else {
                            doc.rect(cellX, cellY, colW, rowH);

                            // Date Number (Top Right)
                            doc.setTextColor(0, 0, 0);
                            doc.setFontSize(10 * scale);
                            doc.setFont(FONT_HELCH, 'bold');
                            doc.text(String(curDate), cellX + colW - (0.05 * scale), cellY + (0.15 * scale), { align: 'right' });

                            // Collect Events (Pacific Time check)
                            const evs = content.events?.filter(e => {
                                const pd = toAdHocPacificDate(e.date);
                                return pd.getUTCDate() === curDate;
                            }) || [];
                            evs.forEach(e => eventsList.push(e));

                            if (evs.length > 0) {
                                let ey = cellY + (0.3 * scale);
                                doc.setFontSize(7 * scale);
                                doc.setFont(FONT_HELCH, 'normal');
                                doc.setTextColor(50, 50, 50);

                                evs.slice(0, 3).forEach(ev => {
                                    const title = ev.title.length > 15 ? ev.title.substring(0, 13) + '..' : ev.title;
                                    doc.text(title, cellX + (0.05 * scale), ey);
                                    ey += (0.12 * scale);
                                });
                            }
                            curDate++;
                        }
                    }
                }

                // 4. Details List (Dynamic Columns & Sizing)
                const listY = gridY + (numRows * rowH) + (0.3 * scale);
                const listBottom = y + faceH - footerH - padding;
                const listDetailH = listBottom - listY - (0.3 * scale); // Available height for items, minus title

                doc.setFontSize(12 * scale);
                doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text("Events & Activities", contentX, listY);

                eventsList.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                const uniqueEvents = Array.from(new Set(eventsList.map(e => e.id)))
                    .map(id => eventsList.find(e => e.id === id)!);

                if (uniqueEvents.length > 0) {
                    // Fits Strategy
                    const strategies = [
                        { cols: 2, fontScale: 1, itemH: 0.35 * scale },
                        { cols: 3, fontScale: 1, itemH: 0.35 * scale },
                        { cols: 3, fontScale: 0.85, itemH: 0.30 * scale }, // Try smaller font 3 cols first
                        { cols: 4, fontScale: 0.85, itemH: 0.30 * scale },
                        { cols: 4, fontScale: 0.7, itemH: 0.25 * scale }   // Emergency compact
                    ];

                    let selectedStrat = strategies[0];

                    for (const strat of strategies) {
                        const itemsPerCol = Math.floor(listDetailH / strat.itemH);
                        const capacity = itemsPerCol * strat.cols;
                        if (uniqueEvents.length <= capacity) {
                            selectedStrat = strat;
                            break;
                        }
                        // Default to last if none fit perfectly (will truncate)
                        selectedStrat = strat;
                    }

                    // Render with selected strategy
                    const { cols, fontScale, itemH } = selectedStrat;
                    let colIndex = 0;
                    let colBaseX = contentX + (0.1 * scale);
                    const colW = (contentW / cols) - (0.1 * scale);

                    let curY = listY + (0.3 * scale);
                    const startY = curY;

                    for (const ev of uniqueEvents) {
                        // Check Column Overflow
                        if (curY + itemH > listBottom) {
                            colIndex++;
                            if (colIndex >= cols) break; // Hard clip if really too many
                            curY = startY;
                            colBaseX = contentX + (0.1 * scale) + (colIndex * (contentW / cols));
                        }

                        const d = toAdHocPacificDate(ev.date);
                        // Using UTC methods on AdHocPacificDate gives us the Pacific values
                        const localDate = d.toLocaleDateString('en-US', { day: 'numeric', weekday: 'short', timeZone: 'UTC' });
                        const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });

                        // Line 1: Date & Title
                        doc.setFontSize(9 * scale * fontScale);
                        doc.setTextColor(0, 0, 0);
                        doc.setFont(FONT_HELCH, 'bold');

                        // Truncate Title visually if needed?
                        const maxTitleLen = colW / (0.05 * scale * fontScale); // Approx chars
                        let line1 = `${localDate} - ${ev.title}`;
                        if (line1.length > maxTitleLen * 1.5) line1 = line1.substring(0, Math.floor(maxTitleLen * 1.5)) + '..'; // Rough truncation

                        doc.text(line1, colBaseX, curY);

                        // Line 2: Time & Location
                        doc.setFontSize(8 * scale * fontScale);
                        doc.setTextColor(100, 100, 100);
                        doc.setFont(FONT_HELCH, 'normal');
                        const line2 = `${time} @ ${ev.location || 'Field'}`;
                        doc.text(line2, colBaseX, curY + (0.14 * scale * fontScale));

                        curY += itemH;
                    }
                }

                // 5. FOOTER
                const footerY = y + faceH - (footerH / 2);
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.005);
                doc.line(contentX, y + faceH - footerH, contentX + contentW, y + faceH - footerH);

                if (content.clubInfo?.clubLogoUrl) {
                    try {
                        const fLogoH = 0.5 * scale;
                        await drawImageContain(doc, content.clubInfo.clubLogoUrl, contentX, y + faceH - footerH + (0.15 * scale), fLogoH, fLogoH);
                        doc.setFontSize(8 * scale);
                        doc.setTextColor(100, 100, 100);
                        doc.setFont(FONT_HELCH, 'bold'); // Revert to bold for clearer text
                        doc.text(content.clubInfo.clubName || 'SRAC', contentX + fLogoH + (0.2 * scale), footerY + (0.1 * scale));
                    } catch (e) { }
                }

                if (content.clubInfo?.clubWebsite) {
                    doc.setFontSize(8 * scale);
                    doc.setTextColor(100, 100, 100);
                    const userUrl = content.clubInfo.clubWebsite.replace(/^https?:\/\//, '');
                    doc.text(userUrl, contentX + contentW, footerY + (0.1 * scale), { align: 'right' });
                }
                break;

            case 'notes':
                doc.setFillColor(255, 255, 255);
                doc.rect(x, y, faceW, faceH, 'F');
                doc.setTextColor(150, 150, 150);
                doc.setFontSize(16 * scale);
                doc.setFont(FONT_HELCH, 'bold');
                doc.text("Notes", x + faceW / 2, y + (1 * scale), { align: 'center' });

                // Lines
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.005); // Thin Lines
                for (let ly = 2; ly < faceH - 1; ly += 0.5) {
                    doc.line(x + (1 * scale), y + (ly * scale), x + faceW - (1 * scale), y + (ly * scale));
                }
                break;

            case 'blank':
                // Debugging help: Light X
                // doc.setDrawColor(240, 240, 240);
                // doc.line(x, y, x + faceW, y + faceH);
                // doc.line(x, y + faceH, x + faceW, y);
                break;
        }

        doc.restoreGraphicsState();
    };

    // Render Loop (7 Sheets)
    const totalSheets = 7;
    for (let s = 1; s <= totalSheets; s++) {
        if (s > 1) doc.addPage();

        // Booklet Imposition Pairs (28 pages)
        // Sheet 1: Front [28, 1], Back [2, 27]
        // Sheet 2: Front [26, 3], Back [4, 25]
        // ...

        // Formula:
        // Outer Face (Front of Sheet): Left=Back Page, Right=Front Page.
        // Wait. Saddle Stitch standard:
        // Sheet 1 Front: Left=Page N(28), Right=Page 1.
        // Sheet 1 Back: Left=Page 2, Right=Page N-1(27).

        // My Logic:
        // Top Face (Inverted)
        // Bottom Face (Upright)

        // If holding Sheet 1 in Portrait (11x17):
        // Top Half is Back side of the folded book? No.
        // Wall Calendar Fold is Top-Edge Binding (usually spiral) or Center Stitch?
        // Saddle Stitch usually means Stapled in the Center Fold.
        // For a Wall Calendar (Top Image, Bottom Grid):
        // It opens vertically.
        // Center Stitch is horizontal.
        // When hanging on wall: Top Page is upright. Bottom Page is upright.
        // When folded closed: Cover is Page 1. Back is Page 28.

        // Imposition for "Calendar" Style (Open Vertical):
        // Sheet 1 Front:
        // Top Half: Page 28 (Back Cover) - INVERTED (Upside down relative to sheet top)
        // Bottom Half: Page 1 (Cover) - UPRIGHT
        // Verification: If I hold sheet, Top is Upside Down Back Cover. Bottom is Upright Cover.
        // Fold middle. Back Cover is on back. Cover is on front. CORRECT.

        // Sheet 1 Back:
        // Top Half: Page 2 (Jan Img) - INVERTED ?
        // Bottom Half: Page 27 (Notes) - UPRIGHT ?
        // Wait. When you open the cover (Page 1) upward...
        // The *Back* of the Cover Sheet is Page 2 (Top) and Page 27? NO.
        // Page 2 should be the Top of the Spread for Jan.
        // Page 3 should be the Bottom of the Spread for Jan.

        // Let's trace pages:
        // P1 (Cover) -> P2 (Jan Img) -> P3 (Jan Grid) -> P4 (Feb Img) ...

        // Booklet Order pairs (Total 28):
        // (28, 1) -> Sheet 1 Side 1
        // (2, 27) -> Sheet 1 Side 2
        // (26, 3) -> Sheet 2 Side 1
        // (4, 25) -> Sheet 2 Side 2
        // ...

        // My Code has logic:
        // `outTopIdx` (28 - ...), `outBotIdx` (1 + ...)

        const outTopIdx = (28 - 2 * (s - 1)) - 1; // s=1 -> 28-0-1 = 27 (Page 28)
        const outBotIdx = (1 + 2 * (s - 1)) - 1;  // s=1 -> 1+0-1 = 0  (Page 1)

        const inTopIdx = (2 + 2 * (s - 1)) - 1;   // s=1 -> 2+0-1 = 1  (Page 2)
        const inBotIdx = (27 - 2 * (s - 1)) - 1;  // s=1 -> 27-0-1 = 26 (Page 27)

        // Side 1 (Front)
        await renderFace(outTopIdx, 0, 0, s === 1);
        await renderFace(outBotIdx, 0, faceH, false);

        // Side 2 (Back)
        doc.addPage();
        await renderFace(inTopIdx, 0, 0, false);
        await renderFace(inBotIdx, 0, faceH, false);
    }

    doc.save(`srac-calendar-booklet-${paperSize}.pdf`);
}
